import { useState, useMemo } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { CallEvent } from '../../api/telemetry'
import { cn, formatTimestamp } from '../../lib/utils'

const eventTypeColors: Record<string, string> = {
  'call.started': 'bg-success/20 text-success',
  'call.ended': 'bg-text-muted/20 text-text-secondary',
  'call.transcript.user': 'bg-info/20 text-info',
  'call.transcript.bot': 'bg-primary/20 text-primary',
  'call.tool.invoked': 'bg-purple-500/20 text-purple-400',
  'call.tool.result': 'bg-purple-500/20 text-purple-400',
  'call.error': 'bg-error/20 text-error',
}

function getEventColor(eventType: string): string {
  return eventTypeColors[eventType] ?? 'bg-text-muted/10 text-text-muted'
}

function EventTypeBadge({ eventType }: { eventType: string }) {
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap', getEventColor(eventType))}>
      {eventType}
    </span>
  )
}

const PAYLOAD_COLLAPSE_THRESHOLD = 120

function PayloadDisplay({ payloadJson }: { payloadJson: string }) {
  const { t } = useTranslation('calls')
  const [expanded, setExpanded] = useState(false)

  let parsed: unknown
  try {
    parsed = JSON.parse(payloadJson)
  } catch {
    return <span className="font-mono text-xs text-text-muted">{payloadJson}</span>
  }

  const formatted = JSON.stringify(parsed, null, 2)
  const isLong = formatted.length > PAYLOAD_COLLAPSE_THRESHOLD

  if (!isLong) {
    return <pre className="whitespace-pre-wrap font-mono text-xs text-text-secondary">{formatted}</pre>
  }

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="mb-1 inline-flex items-center gap-1 text-xs text-primary hover:text-primary-hover transition-colors"
      >
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {expanded ? t('detail.events.collapse') : t('detail.events.expand')}
      </button>
      {expanded ? (
        <pre className="whitespace-pre-wrap rounded-lg bg-surface p-3 font-mono text-xs text-text-secondary max-h-64 overflow-y-auto">
          {formatted}
        </pre>
      ) : (
        <pre className="truncate font-mono text-xs text-text-muted max-w-md">
          {JSON.stringify(parsed)}
        </pre>
      )}
    </div>
  )
}

export function CallEventLog({ events }: { events: CallEvent[] }) {
  const { t } = useTranslation('calls')
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set())

  const sortedEvents = useMemo(
    () => [...events].sort((a, b) => a.timestamp - b.timestamp),
    [events]
  )

  const eventTypes = useMemo(
    () => Array.from(new Set(sortedEvents.map((e) => e.event_type))),
    [sortedEvents]
  )

  const filteredEvents = useMemo(
    () => activeFilters.size === 0 ? sortedEvents : sortedEvents.filter((e) => activeFilters.has(e.event_type)),
    [sortedEvents, activeFilters]
  )

  const toggleFilter = (type: string) => {
    setActiveFilters((prev) => {
      const next = new Set(prev)
      if (next.has(type)) next.delete(type)
      else next.add(type)
      return next
    })
  }

  return (
    <div className="rounded-xl border border-border bg-surface-elevated p-6">
      <h2 className="mb-4 text-lg font-semibold text-text-primary">{t('detail.events.title')}</h2>

      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap gap-2">
        <button
          onClick={() => setActiveFilters(new Set())}
          className={cn(
            'rounded-full px-3 py-1 text-xs font-medium transition-colors',
            activeFilters.size === 0
              ? 'bg-primary/20 text-primary'
              : 'bg-surface text-text-muted hover:text-text-secondary'
          )}
        >
          {t('detail.events.all', { count: events.length })}
        </button>
        {eventTypes.map((type) => (
          <button
            key={type}
            onClick={() => toggleFilter(type)}
            className={cn(
              'rounded-full px-3 py-1 text-xs font-medium transition-colors',
              activeFilters.has(type) ? getEventColor(type) : 'bg-surface text-text-muted hover:text-text-secondary'
            )}
          >
            {type} ({sortedEvents.filter((e) => e.event_type === type).length})
          </button>
        ))}
      </div>

      {/* Event list */}
      <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
        {filteredEvents.length === 0 ? (
          <p className="text-center text-sm text-text-muted py-8">{t('detail.events.empty')}</p>
        ) : (
          filteredEvents.map((event) => (
            <div key={event.id} className="rounded-lg border border-border/50 bg-surface p-3">
              <div className="flex items-center gap-3 mb-1">
                <span className="font-mono text-xs text-text-muted whitespace-nowrap">
                  {formatTimestamp(event.timestamp)}
                </span>
                <EventTypeBadge eventType={event.event_type} />
              </div>
              {event.payload_json && (
                <div className="mt-2">
                  <PayloadDisplay payloadJson={event.payload_json} />
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
